import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@vd/ui';
import type { ProjectWidget, TicketSummary } from '@vd/types';
import { api } from '../../lib/api';
import { newWidgetReport, reportDestination, serverTime, widgetReportIds } from './reportDestination';
import styles from './WidgetSetupWizard.module.css';
import { formatWhen } from '../../lib/time';

/**
 * The setup wizard's last step (TOADO-975): where a report through this widget
 * lands, and a test report the owner sends themselves through the real widget
 * on their own site. Nothing here sends a report, turns the widget on or
 * changes where reports go; it only says where to look and looks when asked.
 *
 * What counts as the test is a widget report that was not on the board when
 * this step opened (review rounds 1 and 2): the reports there then are read
 * once per opening and set aside, so no clock decides it, and the test is not
 * offered until that read has worked. Drafts are neither: a handoff not yet
 * saved has not landed. A found report is shown with its time, title and
 * column for the owner to recognise, never claimed as theirs.
 *
 * Which path a test takes follows the widget's own setting (review round 2):
 * a visitor through the dialog (resolveIntakeColumnId), the team through the
 * dialog (the same rule), or the team through the button's handoff, which
 * the reporter saves in Toado and which lands in the first work column
 * (materializeHandoff).
 */
type Look = { kind: 'idle' } | { kind: 'looking' } | { kind: 'found'; ticket: TicketSummary } | { kind: 'not_yet' } | { kind: 'failed' };
type Path = 'visitor' | 'team' | 'handoff';

/** The first allowed site that can be opened as it is: a pattern such as https://*.example.com cannot. */
export function openableSite(origins: readonly string[]): string | null {
  return origins.find((origin) => /^https?:\/\/[^*/]+$/.test(origin)) ?? null;
}

/** How a report through this widget is sent, by its settings. */
export function reportPath(widget: Pick<ProjectWidget, 'teamMode' | 'launcher'>): Path {
  if (!widget.teamMode) return 'visitor';
  return widget.launcher?.clickAction === 'handoff' ? 'handoff' : 'team';
}

export function WidgetTestReport({ projectId, widget, enabled, onTurnOn }: {
  projectId: string;
  widget: ProjectWidget;
  /** The widget's on switch as the wizard shows it. */
  enabled: boolean;
  /** Back to the step that turns it on. */
  onTurnOn: () => void;
}) {
  const project = useQuery({ queryKey: ['project', projectId], queryFn: () => api.getProject(projectId) });
  const columns = useQuery({ queryKey: ['columns', projectId], queryFn: () => api.listColumns(projectId) });
  // Each opening of the step reads its own baseline (review round 2): one still in flight from an
  // earlier opening is never reused.
  const [opening] = useState(() => `${Date.now()}-${Math.random()}`);
  const before = useQuery({
    queryKey: ['widget-test-baseline', projectId, opening],
    queryFn: async () => widgetReportIds((await api.listTickets(projectId, 'all')).items),
    staleTime: Infinity,
    gcTime: 0,
  });
  const [look, setLook] = useState<Look>({ kind: 'idle' });
  const site = openableSite(widget.allowedOrigins);
  const path = reportPath(widget);

  if (project.isError || columns.isError) {
    return (
      <div className={styles.stack} data-testid="widget-test-report">
        <h3 className={styles.question}>Try it</h3>
        <p className={styles.warning} role="status">
          The project&apos;s board could not be read just now.
          <Button size="sm" variant="secondary" onClick={() => { void project.refetch(); void columns.refetch(); }}>Try again</Button>
        </p>
      </div>
    );
  }
  if (!project.data || !columns.data) return <p className={styles.muted}>Loading…</p>;
  // The handoff saves as the team's capture does, into the first work column, Bug intake or not
  // (resolveFirstColumnId in store.ts: the same order, an intake-only board landing in its one column).
  const destination = reportDestination(path === 'handoff' ? { bugIntakeEnabled: false } : project.data, columns.data);
  const columnName = destination.kind === 'none' ? null : destination.column.name;
  const board = `/projects/${encodeURIComponent(projectId)}/board`;
  const known = before.data;
  // A team panel report lands where a capture does: the first work column.
  const firstWorkDestination = reportDestination({ bugIntakeEnabled: false }, columns.data);
  const firstWork = firstWorkDestination.kind === 'none' ? null : firstWorkDestination.column.name;

  const lookForIt = async () => {
    if (!known) return;
    setLook({ kind: 'looking' });
    try {
      const found = newWidgetReport((await api.listTickets(projectId, 'all')).items, known);
      setLook(found ? { kind: 'found', ticket: found } : { kind: 'not_yet' });
    } catch {
      setLook({ kind: 'failed' });
    }
  };

  const who = path === 'visitor' ? 'A visitor\'s report' : path === 'team' ? 'A report from your team' : 'A report your team saves from the button';
  const patterns = widget.allowedOrigins.filter((origin) => !openableSite([origin]));

  return (
    <div className={styles.stack} data-testid="widget-test-report">
      <h3 className={styles.question}>Try it</h3>
      <p className={styles.help} data-testid="widget-report-destination">
        {destination.kind === 'intake' && <>{who} lands in <strong>{columnName}</strong> on {project.data.name}&apos;s board. Bug intake is on, so new reports wait there until someone moves them out.</>}
        {destination.kind === 'first' && path === 'handoff' && <>{who} lands in <strong>{columnName}</strong>, the first column on {project.data.name}&apos;s board, as a capture does.</>}
        {destination.kind === 'first' && path !== 'handoff' && <>{who} lands in <strong>{columnName}</strong>, the first column on {project.data.name}&apos;s board{destination.intakeWithoutColumn ? ': Bug intake is on, but no column is marked for it.' : ', since Bug intake is off.'}</>}
        {destination.kind === 'none' && <>{project.data.name}&apos;s board has no columns yet, so a report has nowhere to land. Add one in the project&apos;s Columns settings first.</>}
        {/* Only where the team's own captures land somewhere else (review round 2). */}
        {destination.kind === 'intake' && path === 'visitor' && <> Your team&apos;s reports, sent signed in to Toado, go where a capture goes instead.</>}
      </p>
      {destination.kind === 'none' ? null : !enabled ? (
        <p className={styles.warning} role="status">
          The widget is off, so it takes no reports yet.
          <Button size="sm" variant="secondary" onClick={onTurnOn}>Turn it on</Button>
        </p>
      ) : before.isError ? (
        <p className={styles.warning} role="status">
          The board could not be read to start the check, so a test sent now could not be told apart.
          <Button size="sm" variant="secondary" onClick={() => { void before.refetch(); }}>Try again</Button>
        </p>
      ) : !known ? (
        <p className={styles.muted}>Getting the board ready for a test…</p>
      ) : (
        <>
          <p className={styles.help} data-testid="widget-test-how">
            {path === 'visitor' && <>Send one yourself to see it arrive, as a visitor would: open your site in a private window (so you are not signed in to Toado), press the report button, and say it is a test (for example, write &ldquo;Test report&rdquo;) so nobody takes it for a real one.</>}
            {path === 'team' && <>Send one yourself to see it arrive: open your site signed in to Toado, press the report button, and say it is a test (for example, write &ldquo;Test report&rdquo;) so nobody takes it for a real one.</>}
            {path === 'handoff' && <>Send one yourself to see it arrive: open your site signed in to Toado and press the button. It opens Toado to finish the report: say it is a test, then save it there. It is on the board once saved.</>}
            {/* The widget remembers teammates, and may open its team panel instead (review round 3). */}
            {path !== 'visitor' && firstWork && <> If the widget opens its team panel for you, send it from there: that lands in <strong>{firstWork}</strong>, and counts here too.</>}
            {' '}Nothing is sent for you.
          </p>
          <div className={styles.row}>
            {site && <a className={styles.siteLink} href={site} target="_blank" rel="noopener noreferrer">Open {site}</a>}
            {patterns.length > 0 && <span className={styles.help}>{site ? 'Or open' : 'Open'} a page on your site that matches {patterns.join(' or ')}.</span>}
            <Button size="sm" variant="secondary" onClick={() => { void lookForIt(); }} disabled={look.kind === 'looking'}>
              {look.kind === 'looking' ? 'Looking…' : 'Look for my test report'}
            </Button>
            <Link to={board} className={styles.siteLink}>Open the board</Link>
          </div>
        </>
      )}
      {look.kind === 'found' && (
        <p className={styles.help} role="status" data-testid="widget-test-found">
          {/* In brackets: formatWhen can say "Yesterday 3:12 PM", which no "at" fits (review round 4). */}
          A new report came in through the widget ({formatWhen(serverTime(look.ticket.createdAt))}):{' '}
          <Link to={`/tickets/${encodeURIComponent(look.ticket.id)}`}>&ldquo;{look.ticket.title}&rdquo;</Link>
          {(() => {
            const landed = columns.data.find((column) => column.id === look.ticket.columnId)?.name;
            return landed ? <>, in <strong>{landed}</strong>.</> : '.';
          })()}{' '}
          If that is yours, the widget works.
        </p>
      )}
      {look.kind === 'not_yet' && (
        <p className={styles.warning} role="status" data-testid="widget-test-not-yet">
          No new report from the widget on this board since this step opened. One can take a few seconds{path === 'handoff' ? ', and a handoff is on the board only once it is saved in Toado' : ''}. If you sent one
          and it does not come: check that the button shows on your site (Verify, on Add it to your site), that the
          page&apos;s address is one of the allowed sites, and that the workspace has not reached its report limit
          (Billing shows it). A report moved to another project is not on this board.
        </p>
      )}
      {look.kind === 'failed' && <p className={styles.warning} role="status">The board could not be read just now. Try again.</p>}
    </div>
  );
}
